import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { LifeBuoy, Inbox, Filter } from 'lucide-react';
import {
  Card,
  Button,
  PeriodFilter,
  SearchField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  type StatusTone,
  TABLE_PRESETS,
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  adminTicketService,
  type AdminTicketRowEnriched,
  type TicketStatus,
  type TicketPriority,
  type Department,
  STATUS_LABELS,
} from '@/domains/support/services/adminTicketService';
import { formatDateTime, relativeTime } from '@/shared/utils/format';

const STATUS_TONE: Record<TicketStatus, StatusTone> = {
  open: 'pending',
  pending_customer: 'info',
  closed: 'paid',
};

const PRIORITY_TONE: Record<TicketPriority, StatusTone> = {
  low: 'draft',
  normal: 'info',
  high: 'pending',
  urgent: 'overdue',
};

/** Radix Select cannot hold an empty-string value, so "no filter" needs a token. */
const ANY = '__any__';
/** Assignee filter values that are not a staff id. */
const UNASSIGNED = '__unassigned__';

/**
 * Sprint Admin-B B-2 — SLA state comes from `v_admin_ticket_sla` (business-hour
 * aware via `add_working_hours()`). Returns the display badge, or null when the
 * state is NULL (closed OR already first-responded).
 */
function slaBadge(
  ticket: AdminTicketRowEnriched,
): { label: string; tone: StatusTone } | null {
  if (!ticket.sla_state) return null;
  if (ticket.sla_state === 'breached') return { label: 'SLA breached', tone: 'overdue' };
  if (ticket.sla_state === 'at_risk') {
    const m = ticket.sla_remaining_minutes;
    const label = m !== null && m > 0 ? `SLA in ${m}m` : 'SLA at risk';
    return { label, tone: 'pending' };
  }
  return null;
}

/**
 * F-28. This appended the literal string "SAST" to a `toLocaleString` call with
 * NO `timeZone` option — so it rendered in the viewer's zone under a label
 * asserting otherwise. `formatDateTime` pins Africa/Johannesburg, which makes
 * the label true rather than decorative.
 */
function slaDeadlineTooltip(ticket: AdminTicketRowEnriched): string | undefined {
  if (!ticket.sla_deadline) return undefined;
  return `First response due ${formatDateTime(ticket.sla_deadline)} SAST`;
}

const SLA_SORT_RANK: Record<NonNullable<AdminTicketRowEnriched['sla_state']>, number> = {
  breached: 0,
  at_risk: 1,
  within: 2,
};

function compareBySla(a: AdminTicketRowEnriched, b: AdminTicketRowEnriched): number {
  const ra = a.sla_state ? SLA_SORT_RANK[a.sla_state] : 99;
  const rb = b.sla_state ? SLA_SORT_RANK[b.sla_state] : 99;
  if (ra !== rb) return ra - rb;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

function compareByRecency(a: AdminTicketRowEnriched, b: AdminTicketRowEnriched): number {
  return (
    new Date(b.last_replied_at ?? b.created_at).getTime() -
    new Date(a.last_replied_at ?? a.created_at).getTime()
  );
}

type SortMode = 'sla' | 'recency';

export default function SupportInboxPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<AdminTicketRowEnriched[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('open');
  const [departmentFilter, setDepartmentFilter] = useState<string>(ANY);
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | typeof ANY>(ANY);
  const [assigneeFilter, setAssigneeFilter] = useState<string>(ANY);
  const [sortMode, setSortMode] = useState<SortMode>('sla');

  useEffect(() => {
    let cancelled = false;
    adminTicketService.listDepartments().then((d) => {
      if (!cancelled) setDepartments(d);
    });
    // F-14. The assignee is already on every ticket row (`assigned_staff_id`);
    // only the id->name lookup was missing, which is why the inbox could not
    // show or filter by it. "Unassigned tickets" is a headline KPI on the
    // dashboard and a source in the action queue, so the one surface built for
    // triage was the only one that could not answer it.
    adminTicketService
      .listStaff()
      .then((s) => {
        if (!cancelled) setStaff(s.map((x) => ({ id: x.id, name: x.name })));
      })
      .catch(() => {
        // Non-fatal: the column falls back to showing the raw state below.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminTicketService
      .list({
        status: statusFilter,
        department_id: departmentFilter === ANY ? undefined : departmentFilter,
        priority: priorityFilter === ANY ? undefined : priorityFilter,
        // `search` deliberately NOT sent. It was a dependency of this effect,
        // so every character typed issued a PostgREST round trip — and the
        // server-side filter was an `ilike` on `subject` ALONE, so searching a
        // ticket number or a customer name silently matched nothing. Client-side
        // it costs no request and covers all three (Rule T-3).
      })
      .then((data) => {
        if (!cancelled) {
          setTickets(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, departmentFilter, priorityFilter]);

  const staffById = useMemo(
    () => new Map(staff.map((s) => [s.id, s.name])),
    [staff],
  );

  /**
   * Assignee stays client-side: the service's `assigned_to_me` filter only
   * matches a specific id and cannot express "nobody".
   *
   * `dateMode: 'client'` is lawful here under AR-5 option (a) — `list()` carries
   * no `LIMIT` at all, so the fetched set is genuinely complete (24 tickets,
   * measured 2026-09-13). A ticket list is a STOCK set, so the date control is
   * labelled `Opened`, never "Period" — "open tickets in June" is not a thing,
   * "tickets opened in June" is a different question, and the label is what
   * keeps them apart (Rule T-2).
   */
  const table = useTableFilters<AdminTicketRowEnriched>({
    rows: tickets,
    dateMode: 'client',
    dateLabel: 'Opened',
    dateOf: (t) => t.created_at,
    searchText: (t) => [t.number, t.subject, t.customer_name ?? ''].join(' '),
    facets: useMemo(
      () => [
        (t: AdminTicketRowEnriched) =>
          assigneeFilter === ANY ||
          (assigneeFilter === UNASSIGNED
            ? t.assigned_staff_id === null
            : t.assigned_staff_id === assigneeFilter),
      ],
      [assigneeFilter],
    ),
  });

  const rows = useMemo(
    () => [...table.rows].sort(sortMode === 'sla' ? compareBySla : compareByRecency),
    [table.rows, sortMode],
  );

  const unassignedCount = useMemo(
    () => tickets.filter((t) => t.assigned_staff_id === null).length,
    [tickets],
  );

  const columns = useMemo<UniversalTableColumn<AdminTicketRowEnriched>[]>(
    () => [
      {
        key: 'ticket',
        header: 'Ticket',
        sortValue: (t) => t.number,
        render: (t) => <code className="text-xs text-foreground">{t.number}</code>,
        width: 'w-32',
      },
      {
        key: 'subject',
        header: 'Subject',
        sortValue: (t) => t.subject,
        render: (t) => {
          const sla = slaBadge(t);
          return (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-foreground">{t.subject}</span>
              {sla && (
                <span title={slaDeadlineTooltip(t)}>
                  <StatusBadge tone={sla.tone}>{sla.label}</StatusBadge>
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (t) => t.customer_name,
        render: (t) => (
          <div className="flex flex-col">
            <span className="text-foreground">{t.customer_name}</span>
            {t.customer_email && (
              <span className="text-xs text-muted-foreground">{t.customer_email}</span>
            )}
          </div>
        ),
        hideOnMobile: true,
      },
      {
        // F-14. Unassigned is the actionable state, so it is the one that draws
        // the eye — an assigned ticket has an owner and needs no decision.
        key: 'assignee',
        header: 'Assigned',
        sortValue: (t) => (t.assigned_staff_id ? staffById.get(t.assigned_staff_id) ?? 'zz' : ''),
        render: (t) =>
          t.assigned_staff_id === null ? (
            <StatusBadge tone="pending">Unassigned</StatusBadge>
          ) : (
            <span className="text-sm text-foreground">
              {staffById.get(t.assigned_staff_id) ?? '—'}
            </span>
          ),
        width: 'w-40',
      },
      {
        key: 'department',
        header: 'Department',
        sortValue: (t) => t.department_name ?? '',
        render: (t) => (
          <span className="text-muted-foreground">{t.department_name ?? '—'}</span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'priority',
        header: 'Priority',
        sortValue: (t) => t.priority,
        render: (t) => <StatusBadge tone={PRIORITY_TONE[t.priority]}>{t.priority}</StatusBadge>,
        width: 'w-28',
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (t) => t.status,
        render: (t) => (
          <StatusBadge tone={STATUS_TONE[t.status]} dot>
            {STATUS_LABELS[t.status]}
          </StatusBadge>
        ),
        width: 'w-36',
      },
      {
        key: 'last_reply',
        header: 'Last reply',
        sortValue: (t) => (t.last_replied_at ? new Date(t.last_replied_at) : null),
        render: (t) => (
          <div className="flex flex-col">
            <span className="text-muted-foreground">{relativeTime(t.last_replied_at)}</span>
            {t.last_replied_by_kind && (
              <span className="text-xs text-muted-foreground">by {t.last_replied_by_kind}</span>
            )}
          </div>
        ),
        hideOnMobile: true,
      },
    ],
    [staffById],
  );

  return (
    <UniversalPage
      title={
        <span className="flex items-center gap-2">
          <LifeBuoy className="h-5 w-5" />
          Support inbox
        </span>
      }
      description="All customer tickets across departments. Open tickets are highest priority."
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Support' }]}
    >
      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by status">
              {(['open', 'pending_customer', 'closed', 'all'] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={statusFilter === s ? 'primary' : 'outline'}
                  aria-pressed={statusFilter === s}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === 'all' ? 'All' : STATUS_LABELS[s as TicketStatus]}
                </Button>
              ))}
            </div>
          </div>

          {/* F-25. These were raw <select>s with NO label and — unlike every
              other hand-rolled select in the console — no focus-visible ring at
              all, so a keyboard user had no indication of where they were. The
              Select primitive carries the ring, the 44px target and an
              accessible name from aria-label. */}
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="h-9 w-52" aria-label="Filter by department">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All departments</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} ({d.sla_hours}h SLA)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={priorityFilter}
            onValueChange={(v) => setPriorityFilter(v as TicketPriority | typeof ANY)}
          >
            <SelectTrigger className="h-9 w-40" aria-label="Filter by priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any priority</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-9 w-52" aria-label="Filter by assignee">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Anyone</SelectItem>
              <SelectItem value={UNASSIGNED}>
                Unassigned{unassignedCount > 0 ? ` (${unassignedCount})` : ''}
              </SelectItem>
              {staff.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            label="Search tickets"
            placeholder="Search number, subject, customer"
            className="min-w-[180px] flex-1"
          />

          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Opened"
          />

          <div className="flex items-center gap-1" role="group" aria-label="Sort order">
            <span className="text-xs text-muted-foreground">Sort:</span>
            <Button
              size="sm"
              variant={sortMode === 'sla' ? 'primary' : 'outline'}
              aria-pressed={sortMode === 'sla'}
              onClick={() => setSortMode('sla')}
            >
              Breach-first
            </Button>
            <Button
              size="sm"
              variant={sortMode === 'recency' ? 'primary' : 'outline'}
              aria-pressed={sortMode === 'recency'}
              onClick={() => setSortMode('recency')}
            >
              Newest
            </Button>
          </div>
        </div>
      </Card>

      {/* F-26. This was a hand-rolled <table> whose every <tr> carried
          `cursor-pointer` while only the two <Link>s in the first cells
          navigated — five of seven cells promised a click and did nothing.
          UniversalTable's onRowClick brings the handler, tabIndex and the
          Enter-key path together, and adds sortable headers and hideOnMobile
          that the hand-rolled version never had. */}
      <UniversalTable<AdminTicketRowEnriched>
        data={rows}
        columns={columns}
        getRowKey={(t) => t.id}
        onRowClick={(t) => navigate(`/admin/support/${t.id}`)}
        isLoading={loading}
        error={error}
        /* Rule T-4. */
        caption={loading ? undefined : table.caption}
        empty={{
          icon: Inbox,
          title: tickets.length === 0 ? 'No tickets match' : 'No tickets for this assignee',
          description:
            tickets.length === 0
              ? 'Adjust the filters above or wait for the first customer enquiry.'
              : 'Try "Anyone" in the assignee filter.',
        }}
      />
    </UniversalPage>
  );
}
